// API service for managing ISO Application Field Mappings

import type { 
  ApplicationFieldMapping, 
  CreateApplicationFieldMappingData, 
  ApplicationFieldMappingTestResult,
  GroupedApplicationFieldMappings,
  ApplicationFieldMappingStats,
  SalesforceFieldMetadata
} from '../types'

const API_BASE = '/api'

class FieldMappingService {
  /**
   * Get all application field mappings
   */
  async getAllMappings(activeOnly: boolean = true): Promise<ApplicationFieldMapping[]> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings?active=${activeOnly}`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch mappings: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get application field mappings grouped by category
   */
  async getMappingsGrouped(activeOnly: boolean = true): Promise<GroupedApplicationFieldMappings> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/grouped?active=${activeOnly}`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch grouped mappings: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get available categories
   */
  async getCategories(): Promise<string[]> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/categories`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch categories: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get mappings for a specific category
   */
  async getMappingsForCategory(category: string, activeOnly: boolean = true): Promise<ApplicationFieldMapping[]> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/category/${encodeURIComponent(category)}?active=${activeOnly}`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch mappings for category: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Save application field mappings
   */
  async saveMappings(mappings: CreateApplicationFieldMappingData[]): Promise<ApplicationFieldMapping[]> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ mappings })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to save mappings: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Test field mappings against Salesforce
   */
  async testMappings(mappings: ApplicationFieldMapping[]): Promise<ApplicationFieldMappingTestResult> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ mappings })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to test mappings: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get mapping statistics by category
   */
  async getMappingStats(): Promise<ApplicationFieldMappingStats> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/stats`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch mapping stats: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get form schema for dynamic form generation
   */
  async getFormSchema(): Promise<GroupedApplicationFieldMappings> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/form-schema`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch form schema: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get Salesforce fields for a specific object
   * @param objectName - The Salesforce object name
   * @param includeReference - Whether to include reference fields (default: false)
   */
  async getSalesforceObjectFields(objectName: 'Account' | 'Opportunity' | 'Contact', includeReference: boolean = false): Promise<SalesforceFieldMetadata[]> {
    const url = `${API_BASE}/salesforce/objects/${objectName}/fields${includeReference ? '?includeReference=true' : ''}`
    const response = await fetch(url, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch Salesforce fields: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Map form data to Salesforce objects using field mappings
   */
  async mapFormDataToSalesforce(formData: { [key: string]: any }): Promise<{
    Account: { [key: string]: any }
    Opportunity: { [key: string]: any }
    Contact: { [key: string]: any }
  }> {
    const response = await fetch(`${API_BASE}/iso/submit-deal/map-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ formData })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to map form data: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  // =================================
  // Configuration Management Methods
  // =================================

  /**
   * Get the configured account link field for opportunities
   */
  async getAccountLinkField(): Promise<{ fieldName: string | null; configured: boolean }> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/account-link-field`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch account link field configuration: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Set the account link field for opportunities
   */
  async setAccountLinkField(fieldName: string): Promise<{ fieldName: string; description: string }> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/account-link-field`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ fieldName })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to set account link field: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Get all configuration settings
   */
  async getAllConfigurations(): Promise<any[]> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch configurations: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  /**
   * Sync Salesforce field metadata from live API to local database
   */
  async syncSalesforceFields(): Promise<{
    syncedObjects: number
    totalFields: number
    validatedMappings: number
    timestamp: string
  }> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/sync-salesforce-fields`, {
      method: 'POST',
      credentials: 'include'
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to sync Salesforce fields: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data
  }

  // =================================
  // Opportunity Configuration Methods
  // =================================

  /**
   * Get opportunity name template
   */
  async getOpportunityNameTemplate(): Promise<string> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-name-template`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch opportunity name template: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data.template
  }

  /**
   * Set opportunity name template
   */
  async setOpportunityNameTemplate(template: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-name-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ template })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to set opportunity name template: ${response.statusText}`)
    }
  }

  /**
   * Get opportunity default stage
   */
  async getOpportunityDefaultStage(): Promise<string> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-default-stage`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch opportunity default stage: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data.stage
  }

  /**
   * Set opportunity default stage
   */
  async setOpportunityDefaultStage(stage: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-default-stage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ stage })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to set opportunity default stage: ${response.statusText}`)
    }
  }

  /**
   * Get opportunity close days
   */
  async getOpportunityCloseDays(): Promise<number> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-close-days`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch opportunity close days: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data.days
  }

  /**
   * Set opportunity close days
   */
  async setOpportunityCloseDays(days: number): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-close-days`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ days })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to set opportunity close days: ${response.statusText}`)
    }
  }

  /**
   * Get opportunity close date template
   */
  async getOpportunityCloseDateTemplate(): Promise<string> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-close-date-template`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch opportunity close date template: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data.template
  }

  /**
   * Set opportunity close date template
   */
  async setOpportunityCloseDateTemplate(template: string): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-close-date-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ template })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || `Failed to set opportunity close date template: ${response.statusText}`)
    }
  }

  /**
   * Get available template placeholders
   */
  async getTemplatePlaceholders(): Promise<{ [key: string]: string }> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/template-placeholders`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch template placeholders: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data.placeholders
  }

  /**
   * Get Opportunity StageName picklist values from Salesforce
   */
  async getOpportunityStagePicklistValues(): Promise<Array<{ label: string; value: string }>> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-stage-picklist`, {
      credentials: 'include'
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch opportunity stage picklist values: ${response.statusText}`)
    }

    const data = await response.json()
    return data.data.picklistValues
  }

  /**
   * Get opportunity owner configuration
   */
  async getOpportunityOwnerConfig(): Promise<{ fieldName: string; type: 'dynamic' | 'static'; staticValue: string }> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-owner-config`, {
      credentials: 'include'
    })
    
    if (!response.ok) {
      throw new Error('Failed to fetch opportunity owner configuration')
    }
    
    const data = await response.json()
    return {
      fieldName: data.data.fieldName || '',
      type: data.data.type || 'dynamic',
      staticValue: data.data.staticValue || ''
    }
  }

  /**
   * Set opportunity owner configuration
   */
  async setOpportunityOwnerConfig(config: { fieldName: string; type: 'dynamic' | 'static'; staticValue: string }): Promise<void> {
    const response = await fetch(`${API_BASE}/admin/application-field-mappings/config/opportunity-owner-config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(config)
    })
    
    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || 'Failed to save opportunity owner configuration')
    }
  }
}

export const fieldMappingService = new FieldMappingService()
